import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Image } from '@hivebrite/design-system/Image';
import { Link } from '@hivebrite/design-system/Link';

import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import ClampedLinkWorkaround from 'hb-react/shared/components/DesignSystem/ClampedLinkWorkaround';
import useHref from 'hb-react/shared/hooks/useHref';
import useImmersiveMenu from 'hb-react/shared/hooks/useImmersiveMenu';

interface ParentTopicProps {
  parentTopic: {
    cover_picture_small_url: string;
    cover_picture_medium_url: string;
    landing_page: string;
    white_label_url: string | null;
    name: string;
  };
}

const ParentTopic = ({ parentTopic }: ParentTopicProps) => {
  const {
    cover_picture_small_url: coverPictureSmallUrl,
    cover_picture_medium_url: coverPictureMediumUrl,
    landing_page: landingPage,
    white_label_url: whiteLabelURL,
    name,
  } = parentTopic;

  const { currentNetwork } = useDefaultAppContexts();
  const coverPicture =
    coverPictureSmallUrl === '/images/missing/topics/cover_picture/default.jpg'
      ? coverPictureMediumUrl
      : coverPictureSmallUrl;

  const isImmersiveMenu = useImmersiveMenu({
    location: { pathname: landingPage },
    network: currentNetwork,
  });

  const href = useHref(whiteLabelURL, landingPage);

  const forceDefaultLink = !!(whiteLabelURL || isImmersiveMenu);

  return (
    <Box alignItems="center" data-testid="event-parent-topic">
      <ImgStyled src={coverPicture} />
      <Box display="block" ml={3} font="body-semibold-asphalt">
        <ClampedLinkWorkaround
          clamp={2}
          lineHeight=""
          margin={0}
          padding={0}
          title={name}
          useNativeClamp
        >
          <Link to={href} forceDefaultLink={forceDefaultLink} styledOnlyOnHover>
            {name}
          </Link>
        </ClampedLinkWorkaround>
      </Box>
    </Box>
  );
};

export default ParentTopic;

const ImgStyled = styled(Image).attrs({
  width: '100%',
  maxWidth: '128px',
})`
  aspect-ratio: 128 / 68;
  object-fit: cover;
  border-radius: ${({ theme }) =>
    `${theme.themeVariables.globalBorderRadius}px`};
`;
